import { createTheme, ThemeOptions } from '@mui/material/styles';

import { createFontFacesString, hurmeGeometricSansNo4Files } from './fonts';

declare module '@mui/material/styles' {
    interface Theme {
        borderWidth: {
            thin: string;
            medium: string;
        };
        borderRadius: {
            tiny: string;
            verySmall: string;
            small: string;
            medium: string;
            semiLarge: string;
            large: string;
        };
        gradients: {
            primaryRadialGradient: string;
            primaryLinearGradient: string;
            secondaryLinearGradient: string;
        };
        customShadows: {
            primary: string;
            secondary: string;
            primaryGradient: string;
            secondaryGradient: string;
            selectDropdown: string;
        };
    }
    interface ThemeOptions {
        borderWidth?: {
            thin?: string;
            medium?: string;
        };
        borderRadius?: {
            tiny?: string;
            verySmall?: string;
            small?: string;
            medium?: string;
            semiLarge?: string;
            large?: string;
        };
        gradients?: {
            primaryRadialGradient?: string;
            primaryLinearGradient?: string;
            secondaryLinearGradient?: string;
        };
        customShadows?: {
            primary?: string;
            secondary?: string;
            primaryGradient?: string;
            secondaryGradient?: string;
            selectDropdown?: string;
        };
    }
    interface Palette {
        customColors: {
            black: string;
            purple: string;
            lightPurple: string;
            lightGray: string;
            orange: string;
            lightOrange: string;
            white: string;
            violet: string;
        };
    }
    interface PaletteOptions {
        customColors?: {
            black?: string;
            purple?: string;
            lightPurple?: string;
            lightGray?: string;
            orange?: string;
            lightOrange?: string;
            white?: string;
            violet?: string;
        };
    }
}

const themeOptions: ThemeOptions = {
    palette: {
        customColors: {
            black: '#0F0819',
            purple: '#692ABF',
            lightPurple: '#DBD8EA',
            lightGray: '#F2F2F2',
            orange: '#FF4807',
            lightOrange: '#F5835B',
            white: '#FEFEFE',
            violet: '#EFEEF7',
        },
    },
    typography: (palette) => ({
        fontFamily: '"Hurme Geometric Sans No4", "Helvetica", "Arial", sans-serif',
        allVariants: {
            lineHeight: 1,
            letterSpacing: 0,
        },
        h1: {
            fontSize: '1.5rem',
            fontWeight: 600,
            color: palette.customColors.black,
        },
        body2: {
            color: palette.customColors.black,
        },
        caption: {
            fontStyle: 'italic',
        },
    }),
    borderWidth: {
        thin: '1px',
        medium: '2px',
    },
    borderRadius: {
        tiny: '4px',
        verySmall: '8px',
        small: '12px',
        medium: '16px',
        semiLarge: '20px',
        large: '30px',
    },
    gradients: {
        primaryRadialGradient: `radial-gradient(circle at top left, rgba(152, 249, 255, 1) 0%, rgba(255, 255, 255, 0) 60%), radial-gradient(circle at bottom right, rgba(234, 191, 255, 1) 0%, rgba(135, 38, 183, 0) 60%)`,
        primaryLinearGradient: 'linear-gradient(to right, #692ABF, #1E083C)',
        secondaryLinearGradient: 'linear-gradient(to right, #FFAE62, #FF6630)',
    },
    customShadows: {
        primary: '2px 16px 19px 0 rgba(0, 0, 0, 0.09)',
        secondary: '2px 16px 19px 0 rgba(0, 0, 0, 0.2)',
        primaryGradient: '-7px 10px 23px 5px #F5835B, -13px -3px 16px 2px #692ABF',
        secondaryGradient:
            '-1px 6px 9px -2px rgba(245, 132, 91, 0.25), -4px -1px 8.1px -2px rgba(105, 42, 191, 0.5)',
        selectDropdown: '4px 4px 11px 0px rgba(141, 76, 230, 0.25)',
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: `
                ${createFontFacesString('Hurme Geometric Sans No4', hurmeGeometricSansNo4Files)}
            `,
        },
    },
};

const theme = createTheme(themeOptions);

export default theme;
